<div class="flex flex-col h-full bg-card overflow-hidden rounded-xl">
    <!-- Search Header -->
    <div class="flex items-center p-4 border-b">
        <mat-icon class="icon-size-6 text-hint">search</mat-icon>
        <input [formControl]="searchControl" type="text" placeholder="Search applicants..." matInput autocomplete="off"
            class="flex-1 ml-4 text-lg bg-transparent border-none outline-none" autofocus>

        <!-- Loading / Clear Button -->
        <div class="flex items-center ml-auto">
            <mat-progress-spinner *ngIf="isLoading" class="mr-2" [diameter]="20" [mode]="'indeterminate'">
            </mat-progress-spinner>
            <button mat-icon-button *ngIf="searchControl.value" (click)="clearSearch()" class="text-hint">
                <mat-icon class="icon-size-5">close</mat-icon>
            </button>
        </div>
    </div>

    <!-- Results Area -->
    <div class="flex-1 overflow-y-auto bg-gray-50/30">
        <!-- Results List -->
        <div class="divide-y divide-gray-200/60" *ngIf="!isLoading && searchResults.length">
            <div class="group relative flex flex-col p-5 hover:bg-white transition-all cursor-pointer border-l-4 border-transparent hover:border-primary active:bg-gray-50 search-result-card"
                *ngFor="let result of searchResults; let i = index" [class.selected-result]="i === selectedResultIndex"
                (click)="selectResult(result)">

                <div class="flex items-start">
                    <!-- Result Icon/Type indicator -->
                    <div class="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center mr-4 shadow-sm"
                        [ngClass]="{
                        'bg-purple-50 text-purple-600 ring-1 ring-purple-100': result?.module === 'applicant',
                        'bg-blue-50 text-blue-600 ring-1 ring-blue-100': result?.module === 'customer',
                        'bg-green-50 text-green-600 ring-1 ring-green-100': result?.module === 'project',
                        'bg-amber-50 text-amber-600 ring-1 ring-amber-100': result?.module === 'user',
                        'bg-rose-50 text-rose-600 ring-1 ring-rose-100': result?.module === 'task'
                    }">
                        <mat-icon class="icon-size-6" *ngIf="result?.module === 'applicant'">person</mat-icon>
                        <mat-icon class="icon-size-6" *ngIf="result?.module === 'customer'">business</mat-icon>
                        <mat-icon class="icon-size-6" *ngIf="result?.module === 'project'">assignment</mat-icon>
                        <mat-icon class="icon-size-6" *ngIf="result?.module === 'user'">account_circle</mat-icon>
                        <mat-icon class="icon-size-6" *ngIf="result?.module === 'task'">check_circle</mat-icon>
                    </div>

                    <div class="flex-1 min-w-0">
                        <div class="flex items-center justify-between mb-1">
                            <div class="flex flex-col">
                                <a [href]="result?.url"
                                    class="text-lg font-bold text-gray-900 truncate hover:text-primary transition-colors leading-tight">
                                    {{ result?.text }}
                                </a>
                                <div class="flex items-center text-xs text-hint mt-0.5 space-x-2">
                                    <span class="flex items-center" *ngIf="result?.location">
                                        <mat-icon class="icon-size-3.5 mr-1">place</mat-icon> {{ result?.location }}
                                    </span>
                                    <span class="flex items-center" *ngIf="result?.item?.status">
                                        <span class="w-1.5 h-1.5 rounded-full mr-1.5" [ngClass]="{
                                            'bg-green-500': result?.item?.status === 'Active' || result?.item?.status === 'Hired',
                                            'bg-amber-500': result?.item?.status === 'In Review',
                                            'bg-gray-400': !result?.item?.status
                                        }"></span>
                                        {{ result?.item?.status }}
                                    </span>
                                </div>
                            </div>

                            <!-- AI Relevance Score -->
                            <div class="flex flex-col items-end">
                                <div class="flex items-center px-2.5 py-1 rounded-lg text-sm font-bold shadow-sm border transition-all"
                                    [ngClass]="{
                                    'bg-green-50 text-green-700 border-green-200': result?.score >= 0.9,
                                    'bg-amber-50 text-amber-700 border-amber-200': result?.score < 0.9 && result?.score >= 0.7,
                                    'bg-gray-50 text-gray-600 border-gray-200': result?.score < 0.7
                                }">
                                    <mat-icon class="icon-size-4 mr-1.5"
                                        *ngIf="result?.score >= 0.9">auto_awesome</mat-icon>
                                    {{ (result?.score * 100) | number:'1.0-0' }}% Match
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Match Snippet -->
                <div class="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100 group-hover:bg-white group-hover:border-primary/20 transition-all snippet-box"
                    *ngIf="result?.highlight || result?.description">
                    <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                        <mat-icon class="icon-size-3.5 mr-1.5 text-primary">format_quote</mat-icon>
                        Matched Context
                    </div>
                    <div class="text-sm text-gray-700 leading-relaxed snippet-text line-clamp-2"
                        [innerHTML]="result?.highlight || result?.description"></div>

                    <!-- Match Reason (Tooltip Style) -->
                    <div class="mt-3 flex items-start text-xs text-secondary bg-blue-50/50 p-2 rounded border border-blue-100/50"
                        *ngIf="result?.matchReason">
                        <mat-icon class="icon-size-3.5 mr-1.5 text-blue-500 mt-0.5">psychology</mat-icon>
                        <span class="italic font-medium">{{ result?.matchReason }}</span>
                    </div>
                </div>

                <!-- Skills Alignment -->
                <div class="mt-4 flex flex-wrap gap-2" *ngIf="result?.skills?.length">
                    <span *ngFor="let skill of result.skills"
                        class="px-2 py-1 rounded-md bg-white border border-gray-200 text-xs font-medium text-gray-600 hover:border-primary/30 transition-colors">
                        {{ skill }}
                    </span>
                </div>

                <!-- Navigation Indicator (shown on hover) -->
                <div
                    class="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <mat-icon class="icon-size-6 text-primary">chevron_right</mat-icon>
                </div>
            </div>
        </div>

        <!-- Loading Skeletons -->
        <div class="p-4 space-y-4" *ngIf="isLoading">
            <div class="flex items-center space-x-4 animate-pulse" *ngFor="let i of [1,2,3,4,5]">
                <div class="rounded-lg bg-gray-200 h-10 w-10"></div>
                <div class="flex-1 space-y-2 py-1">
                    <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
            </div>
        </div>

        <!-- No Results -->
        <div class="flex flex-col items-center justify-center p-12 text-center"
            *ngIf="!isLoading && searchControl.value && !searchResults.length">
            <mat-icon class="icon-size-20 text-hint mb-4">search_off</mat-icon>
            <div class="text-xl font-semibold text-secondary">No results found</div>
            <div class="text-hint mt-1">We couldn't find anything matching "{{ searchControl.value }}"</div>
        </div>

        <!-- Initial/Empty state -->
        <div class="flex flex-col items-center justify-center p-12 text-center"
            *ngIf="!isLoading && !searchControl.value">
            <mat-icon class="icon-size-20 text-hint mb-4 opacity-20">search</mat-icon>
            <div class="text-xl font-semibold text-secondary opacity-50">Search Everything</div>
            <div class="text-hint mt-1">Type something to start searching across all applicants</div>
        </div>
    </div>

    <!-- Footer -->
    <div class="px-4 py-2 border-t bg-gray-50 text-xs text-hint flex items-center justify-between">
        <div class="flex items-center space-x-4">
            <span class="flex items-center"><mat-icon class="icon-size-3 mr-1">keyboard_return</mat-icon> to
                select</span>
            <span class="flex items-center"><mat-icon class="icon-size-3 mr-1">keyboard_arrow_up</mat-icon><mat-icon
                    class="icon-size-3 mr-1">keyboard_arrow_down</mat-icon> to navigate</span>
        </div>
        <div>Press <kbd class="px-1 py-0.5 bg-white border rounded shadow-sm">Esc</kbd> to close</div>
    </div>
</div>